<template>
    <v-dialog
        :model-value="modelValue"
        @update:model-value="onDialogUpdate"
        max-width="720"
        scrollable
        persistent
    >
        <v-card class="create-ticket-modal">
            <v-card-title class="create-ticket-modal__title">
                <span class="create-ticket-modal__title-text">Сообщить о проблеме</span>
                <span class="create-ticket-modal__title-meta">
                    <span class="create-ticket-modal__step-indicator">
                        Шаг {{ currentStep }} из {{ TOTAL_STEPS }}
                    </span>
                    <v-btn
                        icon
                        variant="text"
                        size="small"
                        class="create-ticket-modal__close"
                        aria-label="Закрыть"
                        :disabled="loading"
                        @click="onCancel"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </span>
            </v-card-title>

            <v-progress-linear
                :model-value="(currentStep / TOTAL_STEPS) * 100"
                color="primary"
                height="3"
                class="create-ticket-modal__progress"
            />

            <v-card-text class="create-ticket-modal__body">
                <!-- Step 1: Category -->
                <section v-if="currentStep === 1" class="create-ticket-modal__step">
                    <h3 class="create-ticket-modal__heading">Что у вас случилось?</h3>

                    <!-- Прямой контакт с фаундерами стоит ВЫШЕ категорий: пока
                         поддержка в бете, личка быстрее очереди тикетов, и
                         типичный клик происходит до всякого выбора категории. -->
                    <div class="create-ticket-modal__founders">
                        <p class="create-ticket-modal__founders-lead">
                            <v-icon size="20" color="#0088cc" class="mr-1" aria-hidden="true">
                                mdi-hand-wave
                            </v-icon>
                            Напишите напрямую фаундерам — мы читаем каждое сообщение.
                        </p>
                        <div class="create-ticket-modal__founders-list">
                            <a
                                v-for="founder in founderLinks"
                                :key="founder.handle"
                                :href="founder.url"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="create-ticket-modal__founder-card"
                                :aria-label="`Написать ${founder.name} в Telegram, ${founder.handle} (откроется в новой вкладке)`"
                                @click="onFounderClick"
                                @auxclick="onFounderClick"
                            >
                                <UserAvatar
                                    :avatar-url="founder.avatar"
                                    :display-name="founder.name"
                                    :size="32"
                                    alt=""
                                />
                                <span class="create-ticket-modal__founder-text">
                                    <strong>{{ founder.name }}</strong>
                                    <span class="create-ticket-modal__founder-handle">
                                        {{ founder.handle }}
                                    </span>
                                </span>
                            </a>
                        </div>
                        <p class="create-ticket-modal__founders-note">
                            Мы подставим ваше имя и ID в чат и скопируем текст в буфер обмена —
                            останется описать проблему.
                        </p>
                    </div>

                    <div class="create-ticket-modal__founders-divider">
                        <span>
                            или оставьте обращение на платформе
                            <BetaBadge />
                        </span>
                    </div>

                    <h4 id="ticket-category-label" class="create-ticket-modal__subheading">
                        Выберите категорию
                    </h4>
                    <ul
                        class="create-ticket-modal__categories"
                        role="radiogroup"
                        aria-labelledby="ticket-category-label"
                    >
                        <li
                            v-for="opt in categoryOptions"
                            :key="opt.value"
                            class="create-ticket-modal__category"
                            :class="{ 'is-selected': category === opt.value }"
                        >
                            <label>
                                <input
                                    v-model="category"
                                    type="radio"
                                    name="ticket-category"
                                    :value="opt.value"
                                    class="create-ticket-modal__radio"
                                />
                                <div class="create-ticket-modal__category-text">
                                    <strong>{{ opt.label }}</strong>
                                    <span>{{ opt.description }}</span>
                                </div>
                            </label>
                        </li>
                    </ul>
                </section>

                <!-- Step 2: Linked context (только когда есть контекст; иначе шаг
                     пропускается — без контекста это была пустая плашка) -->
                <section
                    v-else-if="hasContext && currentStep === 2"
                    class="create-ticket-modal__step"
                >
                    <h3 class="create-ticket-modal__heading">Связанные данные</h3>
                    <div v-if="context" class="create-ticket-modal__context-card">
                        <p class="create-ticket-modal__hint">
                            Мы автоматически прикрепим к обращению:
                        </p>
                        <div class="create-ticket-modal__context-row">
                            <span class="create-ticket-modal__context-label">Тип:</span>
                            <strong>{{ contextTypeLabel }}</strong>
                        </div>
                        <div class="create-ticket-modal__context-row">
                            <span class="create-ticket-modal__context-label">ID:</span>
                            <span>{{ context.id }}</span>
                        </div>
                        <p class="create-ticket-modal__context-note">
                            Эксперт, оплата и другие детали подгрузятся автоматически на стороне
                            сервера.
                        </p>
                    </div>
                </section>

                <!-- Step 3: Description + attachments -->
                <section v-else class="create-ticket-modal__step">
                    <h3 class="create-ticket-modal__heading">Опишите проблему</h3>
                    <v-form ref="formRef">
                        <v-text-field
                            v-model="subject"
                            label="Тема"
                            variant="outlined"
                            density="comfortable"
                            :rules="[subjectRule]"
                            :error-messages="fieldErrors.subject"
                            counter="120"
                            :maxlength="120"
                        />

                        <v-textarea
                            v-model="description"
                            label="Что случилось?"
                            variant="outlined"
                            rows="6"
                            auto-grow
                            :rules="[descriptionRule]"
                            :error-messages="fieldErrors.description"
                            counter="4000"
                            :maxlength="4000"
                            hint="Минимум 10 символов. Чем подробнее опишете, тем быстрее мы поможем."
                            persistent-hint
                        />

                        <v-file-input
                            v-model="attachments"
                            label="Прикрепить файлы (необязательно)"
                            variant="outlined"
                            density="comfortable"
                            multiple
                            chips
                            show-size
                            :accept="acceptList"
                            prepend-icon=""
                            prepend-inner-icon="mdi-paperclip"
                            :rules="[attachmentsRule]"
                            :error-messages="
                                attachmentError ? [attachmentError] : fieldErrors.attachments
                            "
                            class="mt-2"
                        />
                        <p class="create-ticket-modal__limits">
                            До 10 файлов, по 25 МБ каждый. Изображения, PDF, документы Word/Excel,
                            TXT. Видео не поддерживаются.
                        </p>

                        <v-alert v-if="generalError" type="error" variant="tonal" class="mt-3">
                            {{ generalError }}
                        </v-alert>
                    </v-form>
                </section>
            </v-card-text>

            <v-card-actions class="create-ticket-modal__actions">
                <v-btn variant="text" @click="onCancel" :disabled="loading"> Отмена </v-btn>
                <v-spacer />
                <v-btn v-if="currentStep > 1" variant="text" @click="goBack" :disabled="loading">
                    Назад
                </v-btn>
                <v-btn
                    v-if="currentStep < TOTAL_STEPS"
                    color="primary"
                    variant="flat"
                    :disabled="!canAdvance"
                    @click="goNext"
                >
                    Далее
                </v-btn>
                <v-btn
                    v-else
                    color="primary"
                    variant="flat"
                    :loading="loading"
                    :disabled="!canSubmit || loading"
                    @click="onSubmit"
                >
                    Отправить
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
    CONTEXT_TYPE_LABELS,
    SUPPORT_ATTACHMENT_ACCEPT,
    SUPPORT_ATTACHMENT_MAX_PER_MESSAGE,
    SupportApiError,
    TICKET_CATEGORIES,
    createTicket,
    validateAttachmentList,
    type CreateTicketContext,
    type CreateTicketResult,
    type TicketCategory,
} from '@/services/support';
import { emitToast } from '@/shared/lib/toast';
import { useStore } from 'vuex';
import BetaBadge from '@/components/ui/BetaBadge.vue';
import UserAvatar from '@/components/common/UserAvatar.vue';
import {
    FOUNDERS,
    buildFounderMessage,
    buildFounderTelegramUrl,
} from '@/shared/lib/founderContact';

const props = defineProps<{
    modelValue: boolean;
    /** Optional context to associate the ticket with (consultation, conference, …). */
    context?: CreateTicketContext | null;
    /** Optional category to preselect on step 1 (driven by host-page context). */
    presetCategory?: TicketCategory;
    /** Optional parent ticket reference for follow-ups. */
    parentTicketPublicId?: string | null;
}>();

const emit = defineEmits<{
    (event: 'update:modelValue', value: boolean): void;
    (event: 'created', payload: { publicId: string; subject: string }): void;
    (event: 'duplicate', payload: { publicId: string; subject: string }): void;
}>();

const currentStep = ref<number>(1);
const category = ref<TicketCategory | null>(null);
const subject = ref<string>('');
const description = ref<string>('');
const attachments = ref<File[]>([]);
const loading = ref<boolean>(false);
const generalError = ref<string>('');
const fieldErrors = ref<Record<string, string[]>>({});
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

const categoryOptions = TICKET_CATEGORIES;
const acceptList = SUPPORT_ATTACHMENT_ACCEPT;

// Шаг 2 («Связанные данные») имеет смысл только когда обращение привязано к
// контексту (личная сессия / платёж / сессия). Для общего обращения шаг
// пропускаем — раньше там была пустая плашка-пустышка (баг 75).
const hasContext = computed<boolean>(() => props.context != null);
const TOTAL_STEPS = computed<number>(() => (hasContext.value ? 3 : 2));

const contextTypeLabel = computed<string>(() => {
    if (!props.context) return '';
    return CONTEXT_TYPE_LABELS[props.context.type] || props.context.type;
});

/* ─────────────────── Прямой контакт с фаундерами ─────────────────── */

// Стор читаем защищённо: модалка монтируется и там, где стора нет (спеки), и
// после clearUser (guards, catch fetchUser) — тогда user === null, а черновик
// просто теряет строку с именем.
const store = useStore();
const currentUser = computed<Record<string, unknown> | null>(
    () => (store?.state?.user as Record<string, unknown> | null) ?? null,
);

// Свой lookup, а не contextTypeLabel: тот при промахе справочника отдаёт
// машинное значение (`order_item`), и оно уехало бы в личку фаундеру.
const founderCategoryLabel = computed<string | null>(() => {
    if (!category.value) return null;
    return categoryOptions.find((opt) => opt.value === category.value)?.label ?? null;
});

const founderContextTypeLabel = computed<string | null>(() => {
    if (!props.context) return null;
    return CONTEXT_TYPE_LABELS[props.context.type] ?? null;
});

const founderMessage = computed<string>(() =>
    buildFounderMessage({
        firstName: currentUser.value?.first_name as string | undefined,
        lastName: currentUser.value?.last_name as string | undefined,
        userId: currentUser.value?.id as string | undefined,
        categoryLabel: founderCategoryLabel.value,
        contextTypeLabel: founderContextTypeLabel.value,
        contextId: props.context?.id ?? null,
    }),
);

const founderLinks = computed(() =>
    FOUNDERS.map((founder) => ({
        ...founder,
        url: buildFounderTelegramUrl(founder.handle, founderMessage.value),
    })),
);

/** Два тоста подряд по 4 секунды — это спам; запись в буфер идемпотентна. */
const COPY_TOAST_COOLDOWN_MS = 2000;
let lastCopyToastAt = 0;

/**
 * Копирование черновика в буфер как сайд-эффект перехода по ссылке.
 *
 * ЗДЕСЬ ЗАПРЕЩЁН `await` и `async`: WebKit привязывает право на запись в буфер
 * к user activation, и первый же await его съедает. По той же причине не
 * подменяем ссылку на window.open с preventDefault — попап-блокировщик срежет
 * переход. Переход выполняет сам <a>, мы только копируем.
 */
const onFounderClick = (): void => {
    const text = founderMessage.value;
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;

    let writing: Promise<void>;
    try {
        writing = navigator.clipboard.writeText(text);
    } catch {
        // Экзотические WebView умеют бросать синхронно.
        return;
    }

    writing.then(
        () => {
            // Вкладка Telegram уже в фокусе — тост в покинутой вкладке никто не
            // увидит, а смысл продублирован статичной подписью под карточками.
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
            const now = Date.now();
            if (now - lastCopyToastAt < COPY_TOAST_COOLDOWN_MS) return;
            lastCopyToastAt = now;
            emitToast(
                'Текст обращения скопирован в буфер обмена — вставьте, если не подставился сам',
            );
        },
        // Отказ буфера молчит: переход в Telegram уже состоялся, врать тостом нельзя.
        () => undefined,
    );
};

const subjectRule = (value: string): true | string =>
    (!!value && value.trim().length >= 3) || 'Тема от 3 символов.';

const descriptionRule = (value: string): true | string =>
    (!!value && value.trim().length >= 10) || 'Опишите проблему — минимум 10 символов.';

const attachmentError = computed<string>(() => {
    if (!attachments.value || attachments.value.length === 0) return '';
    return validateAttachmentList(attachments.value) ?? '';
});

const attachmentsRule = (value: File[]): true | string => {
    if (!value || value.length === 0) return true;
    if (value.length > SUPPORT_ATTACHMENT_MAX_PER_MESSAGE) {
        return `Максимум ${SUPPORT_ATTACHMENT_MAX_PER_MESSAGE} файлов.`;
    }
    return validateAttachmentList(value) ?? true;
};

const canAdvance = computed<boolean>(() => {
    if (currentStep.value === 1) {
        return category.value !== null;
    }
    if (currentStep.value === 2) {
        return true; // step 2 is informational
    }
    return false;
});

const canSubmit = computed<boolean>(() => {
    if (!category.value) return false;
    if (subject.value.trim().length < 3) return false;
    if (description.value.trim().length < 10) return false;
    if (attachmentError.value) return false;
    return true;
});

const reset = (): void => {
    currentStep.value = 1;
    category.value = props.presetCategory ?? null;
    subject.value = '';
    description.value = '';
    attachments.value = [];
    generalError.value = '';
    fieldErrors.value = {};
    loading.value = false;
};

watch(
    () => props.modelValue,
    (open) => {
        if (open) {
            reset();
        }
    },
    { immediate: true },
);

watch(
    () => props.presetCategory,
    (preset) => {
        // If host page changes its preselected category while the modal is closed,
        // pick it up next time the modal opens (via reset). When the modal is open,
        // honor the new value too — but only if the user hasn't already chosen.
        if (props.modelValue && preset && category.value === null) {
            category.value = preset;
        }
    },
);

const onDialogUpdate = (open: boolean): void => {
    if (!open && !loading.value) {
        emit('update:modelValue', false);
    }
};

const onCancel = (): void => {
    if (loading.value) return;
    emit('update:modelValue', false);
};

const goNext = (): void => {
    if (!canAdvance.value || currentStep.value >= TOTAL_STEPS.value) return;
    currentStep.value += 1;
};

const goBack = (): void => {
    if (currentStep.value <= 1) return;
    currentStep.value -= 1;
};

const onSubmit = async (): Promise<void> => {
    if (!canSubmit.value || loading.value) return;
    const validation = await formRef.value?.validate();
    if (validation && !validation.valid) {
        return;
    }
    loading.value = true;
    generalError.value = '';
    fieldErrors.value = {};
    try {
        if (!category.value) return;
        const result: CreateTicketResult = await createTicket({
            category: category.value,
            subject: subject.value.trim(),
            description: description.value.trim(),
            context: props.context ?? null,
            attachments: attachments.value.length ? Array.from(attachments.value) : undefined,
            parent_ticket_public_id: props.parentTicketPublicId ?? null,
        });
        const ticket = result.ticket;
        if (result.duplicate) {
            emit('duplicate', { publicId: ticket.public_id, subject: ticket.subject });
        } else {
            emit('created', { publicId: ticket.public_id, subject: ticket.subject });
            emitToast(`Обращение ${ticket.public_id} создано.`, 'success');
        }
        emit('update:modelValue', false);
    } catch (error) {
        if (error instanceof SupportApiError) {
            fieldErrors.value = error.fieldErrors;
            generalError.value = error.message;
        } else {
            generalError.value = (error as Error).message || 'Не удалось создать обращение.';
        }
    } finally {
        loading.value = false;
    }
};
</script>

<style scoped>
.create-ticket-modal__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
}

.create-ticket-modal__title-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.create-ticket-modal__close {
    /* компактный крестик, не растягиваем по высоте title */
    margin: -4px -8px -4px 0;
}

.create-ticket-modal__step-indicator {
    font-size: 13px;
    color: #79747e;
    font-weight: 400;
}

.create-ticket-modal__progress {
    margin: 0;
}

.create-ticket-modal__body {
    min-height: 320px;
    padding-top: 16px !important;
}

.create-ticket-modal__step {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.create-ticket-modal__heading {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1d1b20;
}

.create-ticket-modal__hint {
    margin: 0;
    color: #49454f;
    font-size: 14px;
    line-height: 1.4;
}

.create-ticket-modal__categories {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 10px;
}

@media (max-width: 600px) {
    .create-ticket-modal__categories {
        grid-template-columns: 1fr;
    }
}

.create-ticket-modal__category {
    background: #ffffff;
    border: 1px solid #e3e0ec;
    border-radius: 12px;
    transition:
        border-color 120ms ease,
        background 120ms ease;
}

.create-ticket-modal__category:hover {
    border-color: rgba(103, 80, 164, 0.4);
    background: rgba(103, 80, 164, 0.04);
}

.create-ticket-modal__category.is-selected {
    border-color: #6750a4;
    background: rgba(103, 80, 164, 0.08);
}

.create-ticket-modal__category label {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    cursor: pointer;
    width: 100%;
}

.create-ticket-modal__radio {
    accent-color: #6750a4;
    margin-top: 4px;
}

.create-ticket-modal__category-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.create-ticket-modal__category-text strong {
    color: #1d1b20;
    font-size: 14px;
    font-weight: 600;
}

.create-ticket-modal__category-text span {
    color: #79747e;
    font-size: 12px;
    line-height: 1.4;
}

.create-ticket-modal__context-card {
    background: rgba(103, 80, 164, 0.06);
    border: 1px solid rgba(103, 80, 164, 0.18);
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.create-ticket-modal__context-card--empty {
    background: #f6f4fb;
    border-color: #e3e0ec;
    color: #49454f;
    font-size: 14px;
    line-height: 1.45;
}

.create-ticket-modal__context-row {
    display: flex;
    gap: 8px;
    align-items: baseline;
    font-size: 14px;
}

.create-ticket-modal__context-label {
    color: #79747e;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
    min-width: 56px;
}

.create-ticket-modal__context-note {
    margin: 4px 0 0;
    color: #49454f;
    font-size: 13px;
    line-height: 1.4;
}

.create-ticket-modal__limits {
    color: #79747e;
    font-size: 12px;
    margin: 4px 0 0;
    line-height: 1.4;
}

.create-ticket-modal__actions {
    padding: 12px 24px 16px;
}

/* ──────────────────────── Founders escape hatch ─────────────────────── */

.create-ticket-modal__founders {
    padding: 14px;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(0, 136, 204, 0.06) 0%, rgba(103, 80, 164, 0.04) 100%);
    border: 1px solid rgba(0, 136, 204, 0.18);
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.create-ticket-modal__founders-note {
    margin: 0;
    color: #49454f;
    font-size: 12px;
    line-height: 1.4;
}

.create-ticket-modal__subheading {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1d1b20;
}

.create-ticket-modal__founders-divider {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #79747e;
    font-size: 13px;
}

.create-ticket-modal__founders-divider span {
    /* Бейдж BETA не должен отрываться от текста при переносе. */
    flex: 0 1 auto;
    text-align: center;
}

.create-ticket-modal__founders-divider::before,
.create-ticket-modal__founders-divider::after {
    content: '';
    /* Гарантированный минимум: длинная копия не должна схлопывать линии в ноль. */
    flex: 1 0 16px;
    height: 1px;
    background: rgba(121, 116, 126, 0.3);
}

.create-ticket-modal__founders-lead {
    margin: 0;
    color: #1d1b20;
    font-size: 14px;
    line-height: 1.45;
    display: flex;
    align-items: center;
}

.create-ticket-modal__founders-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
}

.create-ticket-modal__founder-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: #ffffff;
    border: 1px solid rgba(0, 136, 204, 0.22);
    border-radius: 12px;
    text-decoration: none;
    color: inherit;
    /* Грид-элемент по умолчанию не сжимается меньше контента — на 320px это
       распирало сетку горизонтально. */
    min-width: 0;
    transition:
        border-color 120ms ease,
        transform 120ms ease,
        box-shadow 120ms ease;
}

.create-ticket-modal__founder-card:hover {
    border-color: rgba(0, 136, 204, 0.55);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 136, 204, 0.12);
}

/* Отдельным правилом: раньше здесь стоял outline:none вместе с hover, и первый
   интерактивный элемент шага оставался без видимого фокуса. */
.create-ticket-modal__founder-card:focus-visible {
    border-color: rgba(0, 136, 204, 0.55);
    outline: 2px solid #006699;
    outline-offset: 2px;
}

@media (forced-colors: active) {
    .create-ticket-modal__founder-card:focus-visible {
        outline: 2px solid CanvasText;
    }
}

.create-ticket-modal__founder-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.create-ticket-modal__founder-text strong {
    color: #1d1b20;
    font-size: 14px;
    font-weight: 600;
}

.create-ticket-modal__founder-handle {
    display: block;
    /* #0088cc даёт на белом 3.9:1 — ниже AA для 13px. Бренд-цвет оставлен
       рамкам и иконкам, где хватает 3:1 для нетекстовых элементов. */
    color: #006699;
    font-size: 13px;
    font-weight: 500;
    /* На 320px хендл длиннее карточки: усекаем, полный вариант живёт в aria-label. */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ── Мобильная адаптация ── */
@media (max-width: 600px) {
    .create-ticket-modal__title {
        font-size: 16px !important;
        padding: 14px 16px 10px !important;
        flex-wrap: wrap;
        gap: 4px;
    }

    .create-ticket-modal__body {
        min-height: 220px;
        padding: 12px 16px 16px !important;
        /* Vuetify scrollable dialog: явный overflow чтобы скролл работал */
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch;
    }

    .create-ticket-modal__step {
        gap: 10px;
    }

    .create-ticket-modal__heading {
        font-size: 16px;
    }

    .create-ticket-modal__category label {
        padding: 10px 12px;
    }

    .create-ticket-modal__category-text strong {
        font-size: 13px;
    }

    .create-ticket-modal__category-text span {
        font-size: 11px;
    }

    /* Блок фаундеров теперь первый на шаге и съедает высоту первого экрана: на
       320×568 без этого сжатия не видно ни одной карточки категории, и экран
       читается как тупик (кнопка «Далее» задизейблена до выбора). */
    .create-ticket-modal__founders {
        padding: 12px;
        gap: 8px;
    }

    .create-ticket-modal__founders-list {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
    }

    .create-ticket-modal__founder-card {
        padding: 8px 10px;
        gap: 8px;
    }

    .create-ticket-modal__founders-lead,
    .create-ticket-modal__founders-note {
        font-size: 12px;
    }

    .create-ticket-modal__founders-divider {
        font-size: 12px;
    }
}
</style>
